import React, { useEffect, useState } from "react";

import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import SocialNetworkImg from "assets/images/SocialNetwork.png";
import { ProfilePreviewModal } from "components/modals/Profile-preview-modal/ProfilePreviewModal";
import { useActionHook, useNavigate } from "hooks";
import { useUserActions } from "store";
import { IProfileData } from "store/types/user/social";

import { PersonCard } from "./PersonCard";

export const SocialNetwork: React.FC = () => {
  const { getPaginationProfiles } = useUserActions();
  const navigate = useNavigate();
  const { data: response, loading, error, action } = useActionHook(getPaginationProfiles);
  const [selectedProfile, setSelectedProfile] = useState<IProfileData | null>(null);

  useEffect(() => {
    action(1, 5);
  }, []);

  const profiles = (response?.data ?? []).slice(0, 5);
  const hasLoaded = !loading && !error;
  const isEmpty = hasLoaded && profiles.length === 0;
  const isFull = profiles.length === 5;

  return (
    <Stack
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
        p: "16px",
        gap: "16px"
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#071F35" }}>Social Network</Typography>

      {error && !loading && (
        <Stack
          sx={{
            backgroundColor: "#FFF5F5",
            border: "1px solid #FECDCA",
            borderRadius: "8px",
            p: "16px",
            gap: "4px"
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#496682" }}>
            Network could not be loaded
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#496682" }}>
            Refresh the page or contact support if the issue persists.
          </Typography>
        </Stack>
      )}

      {isEmpty && (
        <Stack sx={{ alignItems: "center", justifyContent: "center", py: "24px", gap: "8px" }}>
          <Box
            component="img"
            src={SocialNetworkImg}
            alt="Social Network"
            sx={{ width: "120px", height: "86px", objectFit: "contain", opacity: 0.6 }}
          />
          <Typography sx={{ fontSize: "13px", color: "#97A6BF" }}>No network members to display yet.</Typography>
        </Stack>
      )}

      {!error && !isEmpty && (
        <Stack direction="row" gap="10px">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: "270px", borderRadius: "8px" }} />
              ))
            : profiles.map((person) => (
                <PersonCard
                  key={person.details.id}
                  person={person}
                  isFull={isFull}
                  onClick={() => setSelectedProfile(person)}
                />
              ))}

          {!loading && (
            <Stack
              sx={{
                borderRadius: "8px",
                ...(isFull ? { flex: 1.5 } : { width: "300px", flexShrink: 0 }),
                maxHeight: "270px",
                backgroundColor: "#F6F8FC",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                p: "12px"
              }}
            >
              <Box
                component="img"
                src={SocialNetworkImg}
                alt="Social Network"
                sx={{ width: "174px", height: "125px", objectFit: "contain" }}
              />
              <Button
                onClick={() => navigate({ route: "social" })}
                variant="outlined"
                size="sm"
                sx={{
                  color: "#071F35",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "20px",
                  textTransform: "none",
                  px: "8px"
                }}
              >
                Show More
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <ProfilePreviewModal profile={selectedProfile} onClose={() => setSelectedProfile(null)} />
    </Stack>
  );
};
